import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Segment,
  Header,
  Table,
  Loader,
  Message,
  Button,
} from "semantic-ui-react";
import Timeline from "./Timeline";
import { calcolaPrezzoLezioniPrivate } from "../../api/privateNewApi";

const Step3 = ({
  onPrevious,
  onNext,
  selectedDates,
  selectedTimeRange,
  dateTimelines,
  isLoadingTimelines,
  numPupils,
  specialitaId,
  startOrario,
  endOrario,
}) => {
  const { t } = useTranslation();

  // State for selected time ranges for each date
  const [dateTimeRanges, setDateTimeRanges] = useState({});

  // State for price calculation
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [isPriceCalculating, setIsPriceCalculating] = useState(false);
  const [isPriceCalculated, setIsPriceCalculated] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    // Code here runs once when the component is mounted
    // For example:
    console.log("Step3 mounted");
  }, []);
  // Auto-select the time range from Step 2 when dateTimelines are loaded
  useEffect(() => {
    if (selectedTimeRange && Object.keys(dateTimelines).length > 0) {
      const newDateTimeRanges = {};
      let allValid = true;

      selectedDates.forEach((date) => {
        const dateStr = date.format("YYYY-MM-DD");
        if (
          isTimeRangeValid(
            selectedTimeRange.start,
            selectedTimeRange.end,
            dateStr
          )
        ) {
          newDateTimeRanges[dateStr] = {
            start: selectedTimeRange.start,
            end: selectedTimeRange.end,
          };
        } else {
          allValid = false;
        }
      });

      setDateTimeRanges(newDateTimeRanges);
    }
  }, [dateTimelines, selectedTimeRange, selectedDates]);

  useEffect(() => {
    console.log("DateTimeRanges updated:", dateTimeRanges);
    if (!firstRender || Object.keys(dateTimeRanges).length === 0) return;
    setFirstRender(false);
    (async () => {
      await handleCalculatePrice();
    })();
  }, [dateTimeRanges]);

  // Handle time slot selection
  const handleTimeSlotClick = (slot, dateStr) => {
    const currentTimeRange = dateTimeRanges[dateStr] || {};

    if (!currentTimeRange.start) {
      setDateTimeRanges({
        ...dateTimeRanges,
        [dateStr]: { start: slot.start, end: null },
      });
    } else if (!currentTimeRange.end) {
      if (slot.start > currentTimeRange.start) {
        // Calculate the actual end time (slot.start + 30 minutes)
        const endTime = new Date(`2000-01-01T${slot.start}:00`);
        endTime.setMinutes(endTime.getMinutes() + 30);
        const endTimeStr = endTime.toTimeString().slice(0, 5);

        // Check if selection is at least 1 hour
        const startTime = new Date(`2000-01-01T${currentTimeRange.start}:00`);
        const duration = (endTime - startTime) / (1000 * 60); // duration in minutes

        if (duration < 60) {
          // Less than 1 hour, don't allow selection
          return;
        }

        // Check if all slots between start and end are available
        if (isTimeRangeValid(currentTimeRange.start, endTimeStr, dateStr)) {
          setDateTimeRanges({
            ...dateTimeRanges,
            [dateStr]: { ...currentTimeRange, end: endTimeStr },
          });
        } else {
          setDateTimeRanges({
            ...dateTimeRanges,
            [dateStr]: { start: slot.start, end: null },
          });
        }
      } else {
        setDateTimeRanges({
          ...dateTimeRanges,
          [dateStr]: { start: slot.start, end: null },
        });
      }
    } else {
      setDateTimeRanges({
        ...dateTimeRanges,
        [dateStr]: { start: slot.start, end: null },
      });
    }

    // Reset price calculation when time slots change
    setCalculatedPrice(null);
    setIsPriceCalculated(false);
    setPriceError(null);
  };

  // Check if a time range is valid (all slots are available)
  const isTimeRangeValid = (start, end, dateStr) => {
    if (!dateTimelines[dateStr]) return false;

    const startTime = new Date(`2000-01-01T${start}:00`);
    const endTime = new Date(`2000-01-01T${end}:00`);

    let current = new Date(startTime);
    while (current < endTime) {
      const timeStr = current.toTimeString().slice(0, 5);
      if (
        !dateTimelines[dateStr].some(
          (slot) => timeStr >= slot.start && timeStr < slot.end
        )
      ) {
        return false;
      }
      current.setMinutes(current.getMinutes() + 30);
    }
    return true;
  };

  // Get selection instruction text
  const getSelectionInstructionText = (dateStr) => {
    const timeRange = dateTimeRanges[dateStr] || {};

    if (!timeRange.start) {
      return (
        t("seleziona_ora_inizio") || "Seleziona l'ora di inizio (minimo 1 ora)"
      );
    } else if (!timeRange.end) {
      return (
        t("seleziona_ora_fine") ||
        "Seleziona l'ora di fine (minimo 1 ora dalla partenza)"
      );
    } else {
      return (
        t("range_completato") ||
        "Range completato - seleziona nuovo orario se necessario"
      );
    }
  };

  // Check if all dates have time ranges selected
  const allDatesHaveTimeRanges = () => {
    if (!selectedDates || selectedDates.length === 0) return false;

    console.log("Checking all dates for time ranges:", selectedDates);
    console.log("DateTimeRanges:", dateTimeRanges);

    return selectedDates.every((date) => {
      const dateStr = date.format("YYYY-MM-DD");
      return (
        dateTimeRanges[dateStr] &&
        dateTimeRanges[dateStr].start &&
        dateTimeRanges[dateStr].end
      );
    });
  };

  // Calculate price function
  const handleCalculatePrice = async () => {
    if (!allDatesHaveTimeRanges()) return;

    setIsPriceCalculating(true);
    setPriceError(null);

    try {
      // Format data for API call
      const requestedSlots = selectedDates.map((date) => {
        const dateStr = date.format("YYYY-MM-DD");
        return {
          date: dateStr,
          startTime: dateTimeRanges[dateStr].start,
          endTime: dateTimeRanges[dateStr].end,
        };
      });

      const result = await calcolaPrezzoLezioniPrivate(
        requestedSlots,
        numPupils,
        specialitaId
      );

      setCalculatedPrice(result);
      setIsPriceCalculated(true);
    } catch (error) {
      console.error("Error calculating price:", error);
      setPriceError(error.message || "Errore nel calcolo del prezzo");
    } finally {
      setIsPriceCalculating(false);
    }
  };

  return (
    <Segment>
      <Header as="h3">{t("programma_lezioni") || "Programma Lezioni"}</Header>

      {isLoadingTimelines ? (
        <Loader
          active
          inline="centered"
          content={t("caricamento_orari") || "Caricamento orari disponibili..."}
        />
      ) : (
        <>
          <Message info>
            <Message.Header>
              {t("orario_base") || "Orario base selezionato"}:
            </Message.Header>
            <p>
              {selectedTimeRange?.start} - {selectedTimeRange?.end}
            </p>
            <p>
              {t("seleziona_orari_specifici") ||
                "Seleziona gli orari specifici per ogni data se necessario"}
            </p>
          </Message>

          {selectedDates.map((date) => {
            const dateStr = date.format("YYYY-MM-DD");
            const dateFormatted = date.format("DD/MM/YYYY");
            const timeRange = dateTimeRanges[dateStr] || {};

            return (
              <Segment key={dateStr}>
                <Header as="h4">{dateFormatted}</Header>

                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {t("seleziona_orario_per_data") ||
                          "Seleziona orario per questa data"}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell>
                        <Timeline
                          timeSlots={dateTimelines[dateStr] || []}
                          tempTimeRange={timeRange}
                          onTimeSlotClick={(slot) =>
                            handleTimeSlotClick(slot, dateStr)
                          }
                          getSelectionInstructionText={() =>
                            getSelectionInstructionText(dateStr)
                          }
                          showConfirmButton={false}
                          minHour={startOrario}
                          maxHour={endOrario}
                        />

                        {timeRange.start && timeRange.end && (
                          <Message success style={{ marginTop: "10px" }}>
                            <Message.Header>
                              {t("orario_selezionato") || "Orario selezionato"}:
                            </Message.Header>
                            <p>
                              {timeRange.start} - {timeRange.end}
                            </p>
                          </Message>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </Segment>
            );
          })}

          {selectedDates.length > 0 && (
            <Message
              warning={!allDatesHaveTimeRanges()}
              success={allDatesHaveTimeRanges()}
              style={{ textAlign: "center" }}
            >
              <Message.Header>
                {allDatesHaveTimeRanges()
                  ? t("tutti_orari_selezionati") ||
                    "Tutti gli orari sono stati selezionati"
                  : t("seleziona_tutti_orari") ||
                    "Seleziona gli orari per tutte le date"}
              </Message.Header>
              <p>
                {allDatesHaveTimeRanges()
                  ? t("pronto_proseguire") || "Sei pronto per proseguire"
                  : t("completa_selezione") ||
                    "Completa la selezione degli orari per continuare"}
              </p>
            </Message>
          )}

          {/* Price calculation section */}
          {allDatesHaveTimeRanges() && (
            <Segment>
              {isPriceCalculated ? (
                <Message positive style={{ textAlign: "right" }}>
                  <Message.Header>
                    {t("prezzo_totale") || "Prezzo calcolato"}
                  </Message.Header>
                  <p>
                    {t("prezzo_totale") || "Prezzo totale"}: €{" "}
                    {(calculatedPrice
                      ? Object.values(calculatedPrice).reduce(
                          (sum, item) => sum + (item.amount || 0),
                          0
                        )
                      : 0
                    ).toFixed(2) || "0.00"}{" "}
                  </p>
                </Message>
              ) : (
                <>
                  {priceError && (
                    <Message negative>
                      <Message.Header>
                        {t("errore_calcolo") || "Errore nel calcolo del prezzo"}
                      </Message.Header>
                      <p>{priceError}</p>
                    </Message>
                  )}
                  <Button
                    primary
                    onClick={handleCalculatePrice}
                    disabled={isPriceCalculating}
                    loading={isPriceCalculating}
                  >
                    {t("calcola_prezzo") || "Calcola prezzo e procedi"}
                  </Button>
                </>
              )}
            </Segment>
          )}
        </>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <Form.Button onClick={onPrevious}>
          {t("indietro") || "Indietro"}
        </Form.Button>
        <Form.Button
          primary
          onClick={onNext}
          disabled={!allDatesHaveTimeRanges() || !isPriceCalculated}
        >
          {t("agli_allievi") || "Finalizza"}
        </Form.Button>
      </div>
    </Segment>
  );
};

export default Step3;
